import { FormTextInput } from '../../forms';
import { LoadingAnimation } from '../../LoadingAnimation';
import { useDispatch, useSelector } from 'react-redux';
import { SET_CONTENT_STATE } from '../../../store/store';
import { audioContentTableColumnResolver } from '../../../lib/columnResolvers';
import { mutateTrackSearch } from '../../../lib/gqlRequests';
import {
  AudioTrackObject,
  AudioTrackResponseData,
  CatalogSearchField,
  CatalogSearchFieldCounts,
  ContentState,
  GlobalState,
} from '../../../lib/types';
import { requestDataLoader } from '../../../lib/requestDataLoader';
import {
  DataGridPro as DataGrid,
  GridCellEditCommitParams,
  GridColDef,
  GridColumnResizeParams,
  GridSelectionModel,
  GridToolbar,
} from '@mui/x-data-grid-pro';
import { DynamicDownloadButton } from '../../dynamicDownloadButton';
import { ComponentType, useCallback, useMemo, useRef, useState } from 'react';
import { PlayerOverlay, PreviewCell } from '../../PreviewCell';
import { ContentBody, ContentHeader } from '../../wrappers';
import { MEDIA_TYPES } from '../../../lib/constants';
import {
  CATALOG_SEARCH_HINT,
  CATALOG_SEARCH_PLACEHOLDER,
  CatalogSearchFieldTabs,
  rowsFromTrackHits,
} from '../shared/CatalogSearchFieldTabs';

const AUDIO_COLUMN_WIDTH_KEY = 'audioColumnWidth';

export const MusicContentSearch = () => {
  const searchLimit = 500;
  const dispatch = useDispatch();
  const [searchOn, setSearchOn] = useState<CatalogSearchField>('ALL');
  const [fieldCounts, setFieldCounts] = useState<CatalogSearchFieldCounts | null>(
    null
  );
  const searchOnRef = useRef(searchOn);
  searchOnRef.current = searchOn;
  const {
    selectedIds: musicAudioSelectedIds,
    searchInput: musicAudioSearchInput,
    loading: musicAudioLoading,
    searchResult: musicAudioSearchResult,
    error: musicAudioError,
    editEntries,
  } = useSelector<GlobalState, GlobalState['content'][MEDIA_TYPES.MUSIC_AUDIO]>(
    (state) => state['content'][MEDIA_TYPES.MUSIC_AUDIO]
  );

  const onRowSelectedModelCHange = useCallback(
    (m: GridSelectionModel) => {
      dispatch({
        type: SET_CONTENT_STATE[MEDIA_TYPES.MUSIC_AUDIO],
        data: {
          selectedIds: new Set(m.map((e) => Number(e))),
        } as ContentState,
      });
    },
    [dispatch]
  );

  const columns: GridColDef[] = useMemo(() => {
    const cwStr = localStorage.getItem(AUDIO_COLUMN_WIDTH_KEY);
    const columnWidth = cwStr && JSON.parse(cwStr);
    return audioContentTableColumnResolver.map((colResolver) => ({
      field: colResolver.field,
      headerName: colResolver.title,
      editable: colResolver.editable || false,
      type: colResolver.type || 'string',
      width:
        columnWidth && `${colResolver.field}` in columnWidth
          ? columnWidth[colResolver.field]
          : colResolver.flex || 100,
      hideSortIcons: colResolver.field !== 'rankingScore',
      sortable: colResolver.field === 'rankingScore',
      valueFormatter:
        colResolver.field === 'rankingScore'
          ? (params: any) =>
              typeof params.value === 'number' ? params.value.toFixed(3) : '—'
          : undefined,
      headerAlign: 'left',
      cellClassName: (params: any) => {
        if (
          editEntries[params.id] &&
          editEntries[params.id][params.field] != null
        ) {
          return 'edited-cell';
        }
        return '';
      },
      valueGetter: (params) => {
        if (colResolver.prop) {
          return params.row[colResolver.prop];
        } else if (colResolver.resolve) {
          return colResolver.resolve(params.row as AudioTrackObject);
        }
        throw new Error(
          `Column resolver ${colResolver.field} (${colResolver.title}) should have either .prop or .resolve`
        );
      },
    }));
  }, [editEntries]);

  const rows = useMemo(() => {
    return musicAudioSearchResult.map((row) => {
      const edit = editEntries[row.id];
      if (!edit) return row;

      const newRow = { ...row };
      if (edit.tempo != null) {
        newRow.tempo = edit.tempo;
      }
      if (edit.label != null) {
        newRow.label = edit.label;
      }
      if (edit.artistName != null) {
        newRow.artist = newRow.artist || ({} as unknown as any);
        newRow.artist!.name = edit.artistName;
      }
      if (edit.year != null) {
        newRow.releaseYear = edit.year;
      }
      if (edit.name != null) {
        newRow.name = edit.name;
      }

      return newRow;
    });
  }, [editEntries, musicAudioSearchResult]);

  const onEditCellChange = useCallback(
    (c: GridCellEditCommitParams) => {
      const newEditEntries = { ...editEntries };
      newEditEntries[c.id as number] = newEditEntries[c.id as number] || {};
      newEditEntries[c.id as number][c.field] = c.value;

      dispatch({
        type: SET_CONTENT_STATE[MEDIA_TYPES.MUSIC_AUDIO],
        data: {
          editEntries: newEditEntries,
        } as ContentState,
      });
    },
    [dispatch, editEntries]
  );

  const onColumnWidthChanges = useCallback((column: GridColumnResizeParams) => {
    const cwStr = localStorage.getItem(AUDIO_COLUMN_WIDTH_KEY);
    const columnWidth: { [key: string]: number } = cwStr
      ? JSON.parse(cwStr)
      : {};
    columnWidth[column.colDef.field] = column.width;
    localStorage.setItem(AUDIO_COLUMN_WIDTH_KEY, JSON.stringify(columnWidth));
  }, []);

  /**Since DataGrid updates selection model on each render, this workaround is used to prevent cyclic re-rendering */
  const ids = Array.from(musicAudioSelectedIds.values());
  const selectedRows = useMemo(() => {
    return ids;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  const { load } = requestDataLoader<
    AudioTrackResponseData,
    AudioTrackObject[],
    [number, number, string]
  >({
    doRequest: () =>
      mutateTrackSearch({
        limit: searchLimit,
        offset: 0,
        searchText: musicAudioSearchInput,
        searchOn: searchOnRef.current,
      }),
    onStateChange: (output) => {
      dispatch({
        type: SET_CONTENT_STATE[MEDIA_TYPES.MUSIC_AUDIO],
        data: {
          searchResult: output.data,
          error: output.error,
          loading: output.loading,
        } as ContentState,
      });
    },
    initialValue: [],
    identifier: 'musicAudioTracks',
    formatResponse: (result) => {
      setFieldCounts(result.createCatalogSearch.meta?.fieldCounts ?? null);
      return rowsFromTrackHits(result.createCatalogSearch.trackHits);
    },
  });

  const dataGridProps: typeof DataGrid extends ComponentType<infer T>
    ? T
    : unknown = useMemo(() => {
      return {
        checkboxSelection: true,
        columnBuffer: 50,
        density: 'compact',
        components: {
          Toolbar: GridToolbar,
        },
        rows,
        columnTypes: {
          contentUrlButton: {
            renderCell: (p: any) => {
              return (
                <DynamicDownloadButton
                  text={'get file'}
                  id={p.row.id}
                  mediaType={MEDIA_TYPES.MUSIC_AUDIO}
                />
              );
            },
          },
          contentPlaybackButton: {
            renderCell: (p: any) => {
              return (
                <PreviewCell mediaType={MEDIA_TYPES.MUSIC_AUDIO} id={p.row.id} />
              );
            },
          },
        },
        selectionModel: selectedRows,
        onCellEditCommit: (c) => onEditCellChange(c),
        onSelectionModelChange: onRowSelectedModelCHange,
        onColumnWidthChange: onColumnWidthChanges,
        columns,
        loading: musicAudioLoading,
      };
    }, [
      rows,
      selectedRows,
      onEditCellChange,
      onRowSelectedModelCHange,
      columns,
      musicAudioLoading,
      onColumnWidthChanges,
    ]);

  return (
    <ContentBody>
      <ContentHeader>
        <div className="search-button">
          <FormTextInput
            id="searchAudioText"
            type="text"
            inputValue={musicAudioSearchInput}
            placeholder={CATALOG_SEARCH_PLACEHOLDER}
            onChange={(text) => {
              dispatch({
                type: SET_CONTENT_STATE[MEDIA_TYPES.MUSIC_AUDIO],
                data: { searchInput: text } as ContentState,
              });
            }}
            onSubmit={(searchInput) => {
              if (searchInput) {
                dispatch({
                  type: SET_CONTENT_STATE[MEDIA_TYPES.MUSIC_AUDIO],
                  data: {
                    selectedIds: new Set(),
                    editEntries: {},
                  } as ContentState,
                });
                return load(0, searchLimit, searchInput);
              }

              dispatch({
                type: SET_CONTENT_STATE[MEDIA_TYPES.MUSIC_AUDIO],
                data: {
                  selectedIds: new Set(),
                  searchInput: '',
                  error: null,
                  searchResult: [],
                  editEntries: {},
                  loading: false,
                } as Partial<ContentState>,
              });
              setFieldCounts(null);
            }}
          />
        </div>
        <div style={{ paddingLeft: '10px' }}>
          <LoadingAnimation
            sizePx={50}
            color={'black'}
            animate={musicAudioLoading}
          />
        </div>
      </ContentHeader>
      <div style={{ fontSize: 12, color: '#666', padding: '4px 0 0' }}>
        {CATALOG_SEARCH_HINT}
      </div>
      <CatalogSearchFieldTabs
        value={searchOn}
        counts={fieldCounts}
        onChange={(next) => {
          searchOnRef.current = next;
          setSearchOn(next);
          if (musicAudioSearchInput) {
            load(0, searchLimit, musicAudioSearchInput);
          }
        }}
      />

      {musicAudioError && (
        <div className="search-status-msg">Error: {musicAudioError}</div>
      )}

      <div style={{ height: '500px', maxHeight: '100%', width: '100%' }}>
        <DataGrid {...dataGridProps}></DataGrid>
      </div>
      <PlayerOverlay />
    </ContentBody>
  );
};
